// src/components/issues/JiraConfigDialog.jsx - Connect the user's own Jira (OAuth) and choose what to import
import React, { useState, useEffect } from "react";
import ConnectionAccount from "../brain/ConnectionAccount";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { CheckCircle2, AlertCircle, Loader2, Lock } from "lucide-react";
import {
  connectIntegration,
  getIntegrationStatus,
  getIntegrationSettings,
  saveIntegrationSettings,
  selectJiraSite,
  listJiraProjects,
} from "../../lib/integrations";

export default function JiraConfigDialog({ open, onClose, onSave }) {
  const [status, setStatus] = useState(null); // { sites, site_id } when connected
  const [settings, setSettings] = useState({ jql: "", max: 200, projects: [] });
  const [siteId, setSiteId] = useState("");
  const [projects, setProjects] = useState(null); // spaces on the selected site; null while loading
  const [projectsError, setProjectsError] = useState(null);
  const [projectFilter, setProjectFilter] = useState("");
  const [busy, setBusy] = useState(null); // "connect" | "save" | "site" | null
  const [result, setResult] = useState(null);

  const loadProjects = async () => {
    setProjects(null);
    setProjectsError(null);
    try {
      setProjects(await listJiraProjects());
    } catch (error) {
      setProjects([]);
      setProjectsError(error.message);
    }
  };

  const refresh = async () => {
    const [connection, prefs] = await Promise.all([getIntegrationStatus(), getIntegrationSettings()]);
    setStatus(connection.jira);
    setSiteId(connection.jira?.site_id || "");
    setSettings({ ...prefs.jira, projects: prefs.jira.projects || [] });
    if (connection.jira) await loadProjects();
  };

  // Spaces belong to a site, so switching site clears the chosen spaces.
  const changeSite = async (nextSiteId) => {
    setSiteId(nextSiteId);
    setBusy("site");
    setResult(null);
    try {
      await selectJiraSite(nextSiteId);
      setStatus((s) => ({ ...s, site_id: nextSiteId }));
      setSettings((p) => ({ ...p, projects: [] }));
      await loadProjects();
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setBusy(null);
    }
  };

  const toggleProject = (key) => setSettings((p) => ({
    ...p,
    projects: p.projects.includes(key) ? p.projects.filter((k) => k !== key) : [...p.projects, key],
  }));

  useEffect(() => {
    if (!open) return;
    setResult(null);
    refresh().catch((error) => setResult({ success: false, message: error.message }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy("connect");
    setResult(null);
    try {
      await connectIntegration("jira");
      await refresh();
      setResult({ success: true, message: "Jira connected. Choose the spaces to import from, then Save & Import." });
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setBusy(null);
    }
  };

  const saveAndImport = async () => {
    setBusy("save");
    setResult(null);
    try {
      const prefs = { jql: settings.jql.trim() || undefined, max: Number(settings.max) || 200, projects: settings.projects };
      await saveIntegrationSettings("jira", prefs);
      const importCount = await onSave?.({ ...settings, ...prefs });
      setResult({ success: true, message: `✅ Saved! Imported ${importCount || 0} JIRA issues.` });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      setResult({ success: false, message: error.reconnect ? `${error.message}` : `Import failed: ${error.message}` });
      if (error.reconnect) setStatus(null);
    } finally {
      setBusy(null);
    }
  };

  const site = status?.sites?.find((s) => s.id === siteId);
  const visibleProjects = (projects || []).filter((p) => {
    const q = projectFilter.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 520 }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <Lock size={20} color="var(--accent)" />
            Connect to JIRA
          </DialogTitle>
          <DialogDescription>
            Sign in with your own Atlassian account to import issues and epics from your Jira site.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gap: 16 }}>
          {result && (
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: 12,
              borderRadius: 6,
              background: result.success ? "var(--success-soft)" : "var(--danger-soft)",
              border: `1px solid ${result.success ? "var(--success-border)" : "var(--danger-border)"}`,
            }}>
              {result.success ? (
                <CheckCircle2 size={16} color="var(--success)" style={{ marginTop: 2, flexShrink: 0 }} />
              ) : (
                <AlertCircle size={16} color="var(--danger)" style={{ marginTop: 2, flexShrink: 0 }} />
              )}
              <span style={{ fontSize: 13, color: result.success ? "var(--success-fg)" : "var(--danger-fg)" }}>{result.message}</span>
            </div>
          )}

          {!status ? (
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 16, textAlign: "center" }}>
              <Button
                onClick={connect}
                disabled={busy === "connect"}
                style={{ background: "var(--accent-surface)", color: "var(--fg-on-accent)", border: "none", padding: "12px 24px" }}
              >
                {busy === "connect" ? (
                  <><Loader2 size={16} className="animate-spin" style={{ marginRight: 8 }} />Waiting for Atlassian…</>
                ) : (
                  <><Lock size={16} style={{ marginRight: 8 }} />Connect with Atlassian</>
                )}
              </Button>
              <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 8 }}>
                A popup opens where you approve read access to your Jira. SPCC never sees your password.
              </div>
            </div>
          ) : (
            <>
              <div>
                <ConnectionAccount provider="jira" connection={status} disabled={!!busy} onChange={refresh}/>
                <Label htmlFor="jira-site">Jira site</Label>
                {status.sites.length > 1 ? (
                  <select
                    id="jira-site"
                    value={siteId}
                    disabled={busy === "site"}
                    onChange={(e) => changeSite(e.target.value)}
                    style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4, fontSize: 13 }}
                  >
                    {status.sites.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.url})</option>)}
                  </select>
                ) : (
                  <div style={{ fontSize: 14, padding: "6px 0" }}>{site?.name} <span style={{ color: "var(--fg-muted)" }}>{site?.url}</span></div>
                )}
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <Label>Spaces</Label>
                  <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
                    {settings.projects.length ? (
                      <>
                        {settings.projects.length} selected ·{" "}
                        <button
                          type="button"
                          onClick={() => setSettings((p) => ({ ...p, projects: [] }))}
                          style={{ background: "none", border: "none", padding: 0, color: "var(--accent-fg)", cursor: "pointer", fontSize: 12 }}
                        >
                          Clear
                        </button>
                      </>
                    ) : "All spaces"}
                  </span>
                </div>
                {projects === null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--fg-muted)", padding: "6px 0" }}>
                    <Loader2 size={14} className="animate-spin" />Loading spaces…
                  </div>
                ) : projectsError ? (
                  <div style={{ fontSize: 13, color: "var(--danger-fg)", padding: "6px 0" }}>
                    Couldn't load spaces: {projectsError}{" "}
                    <button type="button" onClick={loadProjects} style={{ background: "none", border: "none", padding: 0, color: "var(--accent-fg)", cursor: "pointer", fontSize: 13 }}>
                      Retry
                    </button>
                  </div>
                ) : projects.length === 0 ? (
                  <div style={{ fontSize: 13, color: "var(--fg-muted)", padding: "6px 0" }}>No spaces found on this site.</div>
                ) : (
                  <>
                    {projects.length > 8 && (
                      <Input
                        placeholder="Filter spaces…"
                        value={projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value)}
                        style={{ marginBottom: 6 }}
                      />
                    )}
                    <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 8px" }}>
                      {visibleProjects.map((p) => (
                        <label key={p.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0", cursor: "pointer" }}>
                          <input type="checkbox" checked={settings.projects.includes(p.key)} onChange={() => toggleProject(p.key)} />
                          <span>{p.name}</span>
                          <span style={{ color: "var(--fg-muted)", fontSize: 12 }}>{p.key}</span>
                        </label>
                      ))}
                      {visibleProjects.length === 0 && (
                        <div style={{ fontSize: 13, color: "var(--fg-muted)", padding: "4px 0" }}>No spaces match "{projectFilter}".</div>
                      )}
                    </div>
                  </>
                )}
                <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
                  Import only from the spaces you tick. Leave all unticked to import from every space.
                </div>
              </div>

              <div>
                <Label htmlFor="jira-jql">JQL Filter</Label>
                <Input
                  id="jira-jql"
                  placeholder="updated >= -7d ORDER BY updated DESC"
                  value={settings.jql}
                  onChange={(e) => setSettings((p) => ({ ...p, jql: e.target.value }))}
                />
                <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
                  Applied within the chosen spaces. Leave blank for everything updated in the last 7 days.
                </div>
              </div>

              <div>
                <Label htmlFor="jira-max">Max Issues</Label>
                <Input
                  id="jira-max"
                  type="number"
                  min="1"
                  max="2000"
                  value={settings.max}
                  onChange={(e) => setSettings((p) => ({ ...p, max: parseInt(e.target.value) || 200 }))}
                />
              </div>

              <Button
                onClick={saveAndImport}
                disabled={busy === "save"}
                style={{ background: "var(--success)", color: "white", border: "none" }}
              >
                {busy === "save" ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Importing…</> : "Save & Import"}
              </Button>
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
